import { useState, useEffect, memo } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { tr } from "date-fns/locale/tr";
import { Eye, TrendingUp, Clock } from "lucide-react";
import axios from "axios";
import { useLang } from "../context/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PopularNewsSidebar = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const { lang, t } = useLang();

  useEffect(() => {
    const fetchPopular = async () => {
      try {
        const response = await axios.get(`${API}/news/popular?limit=5&language=${lang}`);
        setNews(response.data);
      } catch (e) {
        console.error("Error fetching popular news:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchPopular();
  }, [lang]);

  if (loading) {
    return (
      <div className="h-full bg-zinc-900/50 backdrop-blur-sm border border-zinc-800/50 rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-amber-500" />
          <h3 className="text-lg font-bold text-white">{t('mostRead')}</h3>
        </div>
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex gap-3 animate-pulse">
              <div className="w-8 h-8 bg-zinc-800 rounded-lg flex-shrink-0" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 bg-zinc-800 rounded w-full" />
                <div className="h-2 bg-zinc-800 rounded w-2/3" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (news.length === 0) {
    return (
      <div className="h-full bg-zinc-900/50 backdrop-blur-sm border border-zinc-800/50 rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-amber-500" />
          <h3 className="text-lg font-bold text-white">{t('mostRead')}</h3>
        </div>
        <p className="text-zinc-500 text-sm text-center py-4">{t('noPopularNews')}</p>
      </div>
    );
  }

  return (
    <div className="h-full bg-zinc-900/50 backdrop-blur-sm border border-zinc-800/50 rounded-2xl p-4 relative overflow-hidden" data-testid="popular-news-sidebar">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-zinc-800/50">
        <div className="w-8 h-8 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center">
          <TrendingUp className="w-4 h-4 text-white" />
        </div>
        <h3 className="text-lg font-bold text-white">{t('mostRead')}</h3>
      </div>

      {/* Popular News List */}
      <div className="space-y-2 relative">
        {news.map((item, index) => (
          <Link
            key={item.id}
            to={`/haber/${item.slug || item.id}`}
            className="flex items-start gap-3 p-2 -mx-2 rounded-lg hover:bg-zinc-800/50 transition-colors group"
            data-testid={`popular-sidebar-${index}`}
          >
            {/* Rank */}
            <div className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold flex-shrink-0 ${
              index === 0 
                ? 'bg-gradient-to-br from-amber-500 to-orange-600 text-white' 
                : index === 1 
                  ? 'bg-zinc-600 text-white'
                  : index === 2
                    ? 'bg-amber-800 text-white'
                    : 'bg-zinc-800 text-zinc-400'
            }`}>
              {index + 1}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <h4 className="text-sm text-white font-medium line-clamp-2 group-hover:text-amber-400 transition-colors leading-tight">
                {item.title}
              </h4>
              <div className="flex items-center gap-2 mt-1 text-xs text-zinc-500">
                <span className="flex items-center gap-1">
                  <Eye className="w-3 h-3" />
                  {item.views}
                </span>
                <span>•</span>
                <span>{format(new Date(item.created_at), "d MMM", { locale: tr })}</span>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Bottom gradient overlay */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-zinc-900/95 via-zinc-900/50 to-transparent pointer-events-none rounded-b-2xl" />
    </div>
  );
};

export default memo(PopularNewsSidebar);
